// Mobile optimization utilities
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768 && window.innerWidth < 1024;
};

export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 1024;
};

// Reduced motion check for accessibility and performance
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Battery-aware animations
export const shouldReduceAnimations = () => {
  if (typeof navigator === 'undefined') return false;
  
  // Check for low battery
  if ('getBattery' in navigator) {
    navigator.getBattery().then(battery => {
      return battery.level < 0.2 || !battery.charging;
    });
  }
  
  // Check for reduced motion preference
  return prefersReducedMotion() || isMobile();
};

// Optimized animation variants for mobile
export const mobileAnimationVariants = {
  // Reduced scale animations for mobile
  scale: {
    mobile: { scale: 1.02 },
    desktop: { scale: 1.05 }
  },
  
  // Shorter durations for mobile
  duration: {
    mobile: 0.2,
    desktop: 0.3
  },
  
  // Reduced translate distances
  translate: {
    mobile: { y: 10 },
    desktop: { y: 20 }
  },
  
  // Battery-friendly spring configs
  spring: {
    mobile: { type: "tween", duration: 0.3 },
    desktop: { type: "spring", stiffness: 260, damping: 20 }
  }
};

// Touch-friendly button configurations
export const touchConfig = {
  minTouchTarget: 44, // Minimum 44px for accessibility
  padding: {
    mobile: 'px-4 py-3',
    desktop: 'px-6 py-4'
  },
  fontSize: {
    mobile: 'text-sm',
    desktop: 'text-base'
  }
};

// Responsive breakpoint utilities
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

export const getBreakpoint = () => {
  if (typeof window === 'undefined') return 'lg';
  
  const width = window.innerWidth;
  if (width < breakpoints.sm) return 'xs';
  if (width < breakpoints.md) return 'sm';
  if (width < breakpoints.lg) return 'md';
  if (width < breakpoints.xl) return 'lg';
  if (width < breakpoints['2xl']) return 'xl';
  return '2xl';
};

// Performance-aware animation configs
export const getAnimationConfig = () => {
  const mobile = isMobile();
  const reducedMotion = prefersReducedMotion();
  
  if (reducedMotion) {
    return {
      duration: 0,
      transition: { duration: 0 }
    };
  }
  
  return {
    duration: mobile ? 0.2 : 0.3,
    transition: mobile 
      ? { type: "tween", duration: 0.2 }
      : { type: "spring", stiffness: 260, damping: 20 }
  };
};

// Optimized scroll behavior
export const scrollConfig = {
  behavior: isMobile() ? 'auto' : 'smooth'
};

// Image optimization for mobile
export const getOptimizedImageSize = (baseSize) => {
  const breakpoint = getBreakpoint();
  const multipliers = {
    xs: 0.6,
    sm: 0.7,
    md: 0.8,
    lg: 1,
    xl: 1.2,
    '2xl': 1.4
  };
  
  return Math.round(baseSize * (multipliers[breakpoint] || 1));
};

// Debounced resize handler for performance
export const createResizeHandler = (callback, delay = 150) => {
  let timeoutId;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
};

// Intersection Observer for mobile performance
export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    rootMargin: isMobile() ? '50px' : '100px',
    threshold: isMobile() ? 0.1 : 0.2,
    ...options
  };
  
  return new IntersectionObserver(callback, defaultOptions);
};